import { Box, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import {
  resolveDocExpirationDays,
  computeExpirationDate,
  daysUntilDate,
} from "../../../../constants/documents/docTypeExpirationDurations";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";

// Shared expiration display used by the upload queue (staged files) and the
// main Documents table. Reads `reportEffectiveDate` +
// `reportGuidelineExpirationDays` (falling back to the doc type's default
// window) off a doc/file object, computes the expiration date + how many days
// remain, and renders one of three interchangeable treatments (see `variant`):
//
//   • "pill"  (default) — expiration date + a color-coded countdown pill
//                         ("69 days left" / "Expires today" / "Expired 5d ago").
//   • "bar"             — expiration date + "in X days" + a thin bar that
//                         DRAINS as the validity window runs down.
//   • "dot"             — a status dot + date + short suffix (most compact).
//
// `nowMs` is the reference day the countdown is measured from. It defaults to
// today, but a CLOSED loan passes its closing date so its docs read as they
// stood at closing instead of drifting into "expired" months later
// (see utils/documents/validityReference).
//
// `closingMs` (optional) is the loan's UPCOMING closing date
// (utils/documents/validityReference → getClosingProjection). When a document
// is still good today but will have lapsed by then, the cell adds a "Before
// closing" flag and the tooltip spells out both answers — expired today? and
// expired at closing? — so a doc that is fine now doesn't read as fine for
// the close.
//
// One `classifyExpiration()` decides the urgency state so every variant colors
// off the same thresholds: > 30 days quiet, 8–30 amber, ≤ 7 red, past-due
// solid red.

const THRESHOLDS = { warning: 30, critical: 7 };

// Urgency bucket from days-remaining. A `null`/absent expiration is handled by
// the caller's guard clauses before this runs.
const classifyExpiration = (remaining) => {
  if (remaining < 0) return "expired";
  if (remaining <= THRESHOLDS.critical) return "critical";
  if (remaining <= THRESHOLDS.warning) return "warning";
  return "ok";
};

// Tailwind emerald/amber/red tokens from the reference design mapped to hex
// (this app has no Tailwind — colors are inlined via sx).
const PILL_STYLES = {
  ok: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  warning: { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" },
  critical: { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5" },
  expired: { bg: "#dc2626", color: "#ffffff", border: "#dc2626" },
};

const BAR_FILL = {
  ok: "#10b981",
  warning: "#f59e0b",
  critical: "#ef4444",
  expired: "#dc2626",
};

const DOT_COLORS = {
  ok: "#10b981",
  warning: "#f59e0b",
  critical: "#ef4444",
  expired: "#dc2626",
};

// Secondary-text color for the "in X days" / suffix lines (bar + dot variants).
const ACCENT_TEXT = {
  ok: grey[500],
  warning: "#d97706",
  critical: "#dc2626",
  expired: "#dc2626",
};

const DATE_TEXT_COLOR = "#1e293b"; // slate-800
const EXPIRED_DATE_COLOR = "#94a3b8"; // slate-400

// The bare countdown pill from the "pill" variant (no date label). Exported so
// other surfaces — e.g. the Documents table filename cell — can render the SAME
// chip that appears in the Expires column instead of a divergent look-alike.
// `remaining` is days-until-expiration (negative = past due); `kind` is the
// urgency bucket from `classifyExpiration`.
export const CountdownPill = ({ kind, remaining, sx }) => {
  const style = PILL_STYLES[kind] || PILL_STYLES.ok;
  const label =
    kind === "expired"
      ? `Exp. ${Math.abs(remaining)}d ago`
      : remaining === 0
        ? "Expires today"
        : `${remaining} days left`;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "3px",
        borderRadius: "999px",
        border: "1px solid",
        borderColor: style.border,
        background: style.bg,
        color: style.color,
        px: "6px",
        py: "1px",
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {(kind === "critical" || kind === "expired") && (
        <WarningAmberOutlined sx={{ fontSize: 11 }} />
      )}
      {label}
    </Box>
  );
};

// Resolve a doc's expiration state the same way the Expires column does. Returns
// `null` when the doc has no expiration window at all; `{ noStart: true }` when
// there's a duration but no effective date yet; otherwise the computed
// `{ days, effective, expiration, remaining, kind }`.
export const getDocExpiration = (doc, nowMs = Date.now()) => {
  const days = resolveDocExpirationDays(doc);
  if (!days) return null;
  const effective = doc?.reportEffectiveDate;
  if (!effective) return { noStart: true, days };
  const expiration = computeExpirationDate(effective, days);
  const remaining = daysUntilDate(expiration, nowMs) ?? 0;
  return {
    days,
    effective,
    expiration,
    remaining,
    kind: classifyExpiration(remaining),
  };
};

// How a doc stands on the CLOSING date, next to how it stands today. Returns
// null when there's no closing date to compare with or the doc has no dated
// window. `lapsesBeforeClosing` is the case worth flagging: not expired
// today, expired by closing.
export const getDocClosingOutlook = (doc, nowMs, closingMs) => {
  if (closingMs == null) return null;
  const today = getDocExpiration(doc, nowMs);
  if (!today || today.noStart) return null;
  const remainingAtClosing = daysUntilDate(today.expiration, closingMs) ?? 0;
  const kindAtClosing = classifyExpiration(remainingAtClosing);
  return {
    remainingAtClosing,
    kindAtClosing,
    lapsesBeforeClosing:
      today.kind !== "expired" && kindAtClosing === "expired",
  };
};

// The "Before closing" flag — amber, since it's a warning about the future
// rather than a document that is already dead (that one gets the solid red
// countdown pill).
export const BeforeClosingPill = ({ sx }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "3px",
      borderRadius: "999px",
      border: "1px dashed",
      borderColor: PILL_STYLES.warning.border,
      background: PILL_STYLES.warning.bg,
      color: PILL_STYLES.warning.color,
      px: "6px",
      py: "1px",
      fontSize: 10,
      fontWeight: 600,
      lineHeight: 1.5,
      whiteSpace: "nowrap",
      flexShrink: 0,
      ...sx,
    }}
  >
    <WarningAmberOutlined sx={{ fontSize: 11 }} />
    Before closing
  </Box>
);

// Tooltip text for a doc measured against today AND the closing date.
// `closingMs` is local midnight of the closing day, so its LOCAL calendar
// fields are the date — read those, never a UTC round-trip.
const ymdOfLocalMs = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

export const closingOutlookTooltip = (outlook, closingMs) => {
  if (!outlook) return null;
  const closingLabel = formatDate(ymdOfLocalMs(closingMs), "", "M/d/yy");
  const r = outlook.remainingAtClosing;
  return outlook.kindAtClosing === "expired"
    ? `At closing (${closingLabel}): expired ${Math.abs(r)} day${Math.abs(r) === 1 ? "" : "s"} before`
    : `At closing (${closingLabel}): valid, ${r} day${r === 1 ? "" : "s"} to spare`;
};

// ── Variant A — date + countdown pill (default / recommended) ──
const PillBody = ({ dateLabel, kind, remaining }) => {
  const isExpired = kind === "expired";
  return (
    <Flx ac g={1} sx={{ minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 11,
          lineHeight: 1.5,
          fontWeight: isExpired ? 400 : 500,
          color: isExpired ? EXPIRED_DATE_COLOR : DATE_TEXT_COLOR,
          textDecoration: isExpired ? "line-through" : "none",
          whiteSpace: "nowrap",
        }}
      >
        {dateLabel}
      </Txt>
      <CountdownPill kind={kind} remaining={remaining} />
    </Flx>
  );
};

// ── Variant B — date + depleting bar (drains as life runs out) ──
const BarBody = ({ dateLabel, kind, remaining, windowDays }) => {
  if (kind === "expired") {
    return (
      <Flx column g={0.25} sx={{ minWidth: 0 }}>
        <Txt
          sx={{
            fontSize: 11,
            color: EXPIRED_DATE_COLOR,
            textDecoration: "line-through",
            whiteSpace: "nowrap",
          }}
        >
          {dateLabel}
        </Txt>
        <Flx ac g={0.5}>
          <WarningAmberOutlined sx={{ fontSize: 11, color: "#dc2626" }} />
          <Txt sx={{ fontSize: 10, fontWeight: 600, color: "#dc2626" }}>
            Expired {Math.abs(remaining)} days ago
          </Txt>
        </Flx>
      </Flx>
    );
  }
  const pct = Math.max(0, Math.min(100, (remaining / (windowDays || 1)) * 100));
  return (
    <Flx column g={0.5} sx={{ minWidth: 0 }}>
      <Flx ac g={1}>
        <Txt sx={{ fontSize: 11, fontWeight: 500, color: DATE_TEXT_COLOR }}>
          {dateLabel}
        </Txt>
        <Txt
          sx={{
            fontSize: 10,
            fontWeight: kind === "ok" ? 400 : 500,
            color: ACCENT_TEXT[kind],
            whiteSpace: "nowrap",
          }}
        >
          {remaining === 0 ? "today" : `in ${remaining} days`}
        </Txt>
      </Flx>
      <Box
        sx={{
          height: 4,
          width: 96,
          borderRadius: "999px",
          bgcolor: "#e2e8f0",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: "999px",
            bgcolor: BAR_FILL[kind],
          }}
        />
      </Box>
    </Flx>
  );
};

// ── Variant C — status dot + single line (most compact) ──
const DotBody = ({ dateLabel, kind, remaining }) => {
  const suffix =
    kind === "expired"
      ? `· expired ${Math.abs(remaining)}d ago`
      : remaining === 0
        ? "· today"
        : `· ${remaining}d left`;
  const isExpired = kind === "expired";
  return (
    <Flx ac g={1} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: DOT_COLORS[kind],
        }}
      />
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 500,
          color: isExpired ? "#dc2626" : DATE_TEXT_COLOR,
          whiteSpace: "nowrap",
        }}
      >
        {dateLabel}
      </Txt>
      <Txt
        sx={{
          fontSize: 10,
          fontWeight: kind === "ok" ? 400 : 500,
          color: ACCENT_TEXT[kind],
          whiteSpace: "nowrap",
        }}
      >
        {suffix}
      </Txt>
    </Flx>
  );
};

const VARIANT_BODIES = {
  pill: PillBody,
  bar: BarBody,
  dot: DotBody,
};

const DocExpirationCell = ({
  doc,
  nowMs = Date.now(),
  closingMs,
  variant = "pill",
}) => {
  const days = resolveDocExpirationDays(doc);
  if (!days) {
    return <Txt sx={{ fontSize: 11, color: grey[400] }}>—</Txt>;
  }
  const effective = doc?.reportEffectiveDate;
  if (!effective) {
    return (
      <Tooltip title={`Valid ${days} days from the effective date`}>
        <Txt sx={{ fontSize: 11, color: grey[500], fontStyle: "italic" }}>
          Requires start date
        </Txt>
      </Tooltip>
    );
  }
  const expiration = computeExpirationDate(effective, days);
  const remaining = daysUntilDate(expiration, nowMs);
  const kind = classifyExpiration(remaining ?? 0);
  const Body = VARIANT_BODIES[variant] || PillBody;
  const outlook = getDocClosingOutlook(doc, nowMs, closingMs);
  const closingLine = closingOutlookTooltip(outlook, closingMs);

  return (
    <Tooltip
      title={
        <>
          {`Effective ${formatDate(effective, "")} → Expires ${formatDate(
            expiration,
            "",
          )}`}
          {closingLine && (
            <>
              <br />
              {closingLine}
            </>
          )}
        </>
      }
    >
      {/* minWidth:0 so the cell content can truncate rather than forcing the
          column wider than its share. */}
      <Flx ac g={0.75} sx={{ minWidth: 0 }}>
        <Body
          // Timezone-stable short date. `formatDate` with a pattern builds a
          // LOCAL date from the Y/M/D calendar fields (no UTC round-trip), so
          // the expiration never shifts back a day in negative-offset zones —
          // unlike the old `formatDateShortYy`/`new Date(...)` path, which
          // parsed the date as UTC midnight then formatted in local (Eastern)
          // time and rendered 7/26 as "7/25".
          dateLabel={formatDate(expiration, "", "M/d/yy")}
          kind={kind}
          remaining={remaining ?? 0}
          windowDays={days}
        />
        {outlook?.lapsesBeforeClosing && <BeforeClosingPill />}
      </Flx>
    </Tooltip>
  );
};

export default DocExpirationCell;
